import { useState } from 'react';
import { observer } from 'mobx-react';
import useAsyncEffect from 'use-async-effect';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Appl } from '../../../../core/Appl';
import { View } from '../../../../core/components/Index';
import RetirementCalcViewModel from './RetirementCalcViewModel';
import { isPepraTier } from './engine/RetirementCalcEngine';
import type { HelpContent } from './engine/data';
import './RetirementCalcView.css';

function toDateInputValue(v?: (string & Date) | null): string {
  if (!v) return '';
  if (v instanceof Date) {
    if (isNaN(v.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`;
  }
  return String(v);
}

function toNumberOrUndefined(raw: string): number | undefined {
  const cleaned = raw.replace(/,/g, '').trim();
  if (!cleaned) return undefined;
  const n = Number(cleaned);
  return isNaN(n) ? undefined : n;
}

const TOOL_DOES_ICON = (
  <svg viewBox="0 0 640 640"><path fill="rgb(85, 113, 34)" d="M320 576C461.4 576 576 461.4 576 320C576 178.6 461.4 64 320 64C178.6 64 64 178.6 64 320C64 461.4 178.6 576 320 576zM288 224C288 206.3 302.3 192 320 192C337.7 192 352 206.3 352 224C352 241.7 337.7 256 320 256C302.3 256 288 241.7 288 224zM280 288L328 288C341.3 288 352 298.7 352 312L352 400L360 400C373.3 400 384 410.7 384 424C384 437.3 373.3 448 360 448L280 448C266.7 448 256 437.3 256 424C256 410.7 266.7 400 280 400L304 400L304 336L280 336C266.7 336 256 325.3 256 312C256 298.7 266.7 288 280 288z" /></svg>
);
const IMPORTANT_NOTES_ICON = (
  <svg viewBox="0 0 640 640"><path fill="rgb(85, 113, 34)" d="M384 64C407.7 64 428.4 76.9 439.4 96L448 96C483.3 96 512 124.7 512 160L512 512C512 547.3 483.3 576 448 576L192 576C156.7 576 128 547.3 128 512L128 160C128 124.7 156.7 96 192 96L200.6 96C211.6 76.9 232.3 64 256 64L384 64zM320 448C302.3 448 288 462.3 288 480C288 497.7 302.3 512 320 512C337.7 512 352 497.7 352 480C352 462.3 337.7 448 320 448zM320 256C301.8 256 287.3 271.5 288.6 289.7L296 393.7C296.9 406.3 307.4 416 319.9 416C332.5 416 342.9 406.3 343.8 393.7L351.2 289.7C352.5 271.5 338.1 256 319.8 256zM264 128C250.7 128 240 138.7 240 152C240 165.3 250.7 176 264 176L376 176C389.3 176 400 165.3 400 152C400 138.7 389.3 128 376 128L264 128z" /></svg>
);
const MYSCERS_ICON = (
  <svg viewBox="0 0 640 640"><path fill="rgb(19, 77, 129)" d="M128 64C92.7 64 64 92.7 64 128L64 512C64 547.3 92.7 576 128 576L308 576C285.3 544.5 272 505.8 272 464C272 363.4 349.4 280.8 448 272.7L448 234.6C448 217.6 441.3 201.3 429.3 189.3L322.7 82.7C310.7 70.7 294.5 64 277.5 64L128 64zM389.5 240L296 240C282.7 240 272 229.3 272 216L272 122.5L389.5 240zM608 464C608 384.5 543.5 320 464 320C384.5 320 320 384.5 320 464C320 543.5 384.5 608 464 608C543.5 608 608 543.5 608 464zM521.4 403.1C528.5 408.3 530.1 418.3 524.9 425.4L460.9 513.4C458.1 517.2 453.9 519.6 449.2 519.9C444.5 520.2 439.9 518.6 436.6 515.3L396.6 475.3C390.4 469.1 390.4 458.9 396.6 452.7C402.8 446.5 413 446.5 419.2 452.7L446 479.5L499 406.6C504.2 399.5 514.2 397.9 521.4 403.1z" /></svg>
);

// Help copy comes from the reference data (database, else the bundled copy in
// engine/data/uiContent.ts).
const HelpButton = ({ content, onOpen }: { content?: HelpContent; onOpen: () => void }) => (content ? (
  <button className="help-trigger field-help-trigger" type="button" aria-label={`Learn more about ${content.title}`} aria-haspopup="dialog" onClick={onOpen}>
    ?
  </button>
) : null);

const HelpModal = ({ content, onClose }: { content?: HelpContent; onClose: () => void }) => {
  if (!content) return null;
  return (
    <div className="ack-modal" role="dialog" aria-modal="true">
      <div className="ack-modal-backdrop" onClick={onClose} />
      <div className="ack-modal-panel myscers-modal" role="document">
        <div className="myscers-modal-header">
          <h3>{content.title}</h3>
          <button className="myscers-modal-close" type="button" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="myscers-modal-body">
          {content.body.map((p, i) => <p key={i}>{p}</p>)}
          <div className="myscers-modal-actions">
            <button className="primary" type="button" onClick={onClose}>Ok</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RetirementCalcView = observer(() => {
  const vm = Appl.Services.get<RetirementCalcViewModel>('RetirementCalcViewModel');
  vm.navigate = useNavigate();
  const { memberId } = useParams();
  const location = useLocation();
  // True for both retirement-calculator/estimate (no id yet — MyScers portal
  // redirect target) and retirement-calculator/estimate/:memberId (the
  // internal, post-resolution URL). False for the plain public route.
  const isAuthenticatedEntry = location.pathname.includes('/retirement-calculator/estimate');
  const [openHelpId, setOpenHelpId] = useState<string | null>(null);
  const hasPriorMembership = !!vm.form.hasPriorMembership;

  useAsyncEffect(async () => {
    await vm.initAsync(memberId, isAuthenticatedEntry);
  }, [memberId, isAuthenticatedEntry]);

  vm.formPath = location.pathname;
  const isDevEnvironment = Appl.Setting?.EnvironmentName !== 'prod';
  // membershipTierOptions is [code, label] pairs — the 9 tiers (M1-M5, S1-S4)
  // the original calculator's UI exposes; from the server once loaded.
  const tierOptions = vm.referenceData.membershipTierOptions.map(([code, label]) => ({ code, label }));
  const taEligible = !!vm.form.tier && !isPepraTier(vm.form.tier);
  const help = vm.referenceData.helpContent;
  const helpButton = (id: string) => <HelpButton content={help[id]} onOpen={() => setOpenHelpId(id)} />;
  const mbaseOverrideActive = !!vm.form.mbaseDetailOverrideEnabled;

  return (
    <View id="retirement-calculator" pageTitle="Retirement Estimate Calculator" container={false} className="">
      <div className="retirement-calc">
        <main className="page">
          <h1 className="page-title">Retirement Benefit Calculator</h1>
          <p className="intro">
            This tool provides a preliminary estimate of your monthly retirement benefit based on the information you provide.
            <br />Actual benefits may differ at retirement.
          </p>

          {vm.isLoadingMemberData && (
            <div className="load-error-banner">Loading your membership information&hellip;</div>
          )}
          {vm.memberDataLoadFailed && (
            <div className="load-error-banner">
              We couldn't load your membership information automatically. You can still fill out the estimate manually below.
            </div>
          )}
          {vm.memberDataAsOf && !vm.memberDataLoadFailed && (
            <div className="data-as-of-banner">
              Some fields are filled in from your SCERS record as of{' '}
              {vm.memberDataAsOf.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.
              Please check them and change anything that should be different for the retirement you're estimating.
            </div>
          )}

          <div className="layout">
            <div className="form-stack">
              <form id="calculatorForm" onSubmit={async (e) => {
                e.preventDefault();
                if (vm.isPreparingCalculation) return;
                if (await vm.calculateAsync()) {
                  window.scrollTo(0, 0);
                  vm.navigate('/retirement-calculator/results');
                } else {
                  setTimeout(() => document.getElementById('resultSection')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
                }
              }}>
                <section className="form-section" id="retirementDetailsSection">
                  <h2>1. Retirement Details</h2>

                  <div className="membership-subhead">
                    <div>
                      <h3>Current / Most Recent Membership</h3>
                      <p>Enter the plan or tier in which you most recently earned service credit.</p>
                    </div>
                  </div>

                  <div className="three-col">
                    <div className="field">
                      <div className="field-label-slot">
                        <label htmlFor="dob">Date Of Birth<sup className="req" aria-label="required">*</sup></label>
                      </div>
                      <div className="field-control-slot">
                        <input id="dob" type="date" max="9999-12-31" required
                          value={toDateInputValue(vm.form.dob)}
                          onChange={(e) => vm.onInputChange(e, 'dob', vm.form)} />
                      </div>
                    </div>
                    <div className="field">
                      <div className="field-label-slot">
                        <label htmlFor="retDate">Planned Retirement Date<sup className="req" aria-label="required">*</sup></label>
                      </div>
                      <div className="field-control-slot">
                        <input id="retDate" type="date" max="9999-12-31" required
                          value={toDateInputValue(vm.form.ret)}
                          onChange={(e) => vm.onInputChange(e, 'ret', vm.form)} />
                      </div>
                    </div>
                    <div className="field">
                      <div className="field-label-slot">
                        <label htmlFor="tier">Current Plan / Tier<sup className="req" aria-label="required">*</sup></label>
                      </div>
                      <div className="field-control-slot">
                        <select id="tier" required value={vm.form.tier ?? ''} onChange={(e) => vm.onInputChange(e, 'tier', vm.form)}>
                          <option disabled value="">Select</option>
                          {tierOptions.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="field">
                      <div className="field-label-slot">
                        <label htmlFor="service" className="label-with-help">
                          <span>Service Credit at Retirement Date<sup className="req" aria-label="required">*</sup></span>
                          {helpButton('service')}
                        </label>
                      </div>
                      <div className="field-control-slot">
                        <div className="control">
                          <input id="service" type="text" inputMode="decimal" autoComplete="off" placeholder="25.00000" required={!mbaseOverrideActive} disabled={mbaseOverrideActive}
                            value={vm.form.serviceRaw ?? ''}
                            onChange={(e) => {
                              e.target.value = e.target.value.replace(/,/g, '');
                              vm.onInputChange(e, 'serviceRaw', vm.form);
                            }} />
                          <span className="suffix">years</span>
                        </div>
                      </div>
                    </div>
                    <div className="field">
                      <div className="field-label-slot">
                        <label htmlFor="comp" className="label-with-help">
                          <span>Estimated Final Monthly Compensation<sup className="req" aria-label="required">*</sup></span>
                          {helpButton('comp')}
                        </label>
                      </div>
                      <div className="field-control-slot">
                        <div className="control">
                          <span className="prefix">$</span>
                          <input id="comp" type="text" inputMode="decimal" autoComplete="off" placeholder="8,500.00" required
                            value={vm.form.comp ?? ''}
                            onChange={(e) => vm.form.comp = toNumberOrUndefined(e.target.value)} />
                        </div>
                      </div>
                    </div>
                    <div className="field">
                      <div className="field-label-slot">
                        <label htmlFor="sickHours" className="label-with-help">
                          <span>Unused Sick Leave Hours</span>
                          {helpButton('sickHours')}
                        </label>
                      </div>
                      <div className="field-control-slot">
                        <div className="control">
                          <input id="sickHours" type="number" min={0} step="0.01" placeholder="50" disabled={mbaseOverrideActive}
                            value={vm.form.sickHours ?? ''}
                            onChange={(e) => vm.form.sickHours = toNumberOrUndefined(e.target.value)} />
                          <span className="suffix">hours</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {isDevEnvironment && (
                    <section className={`mbase-rap-panel membership-override-panel${mbaseOverrideActive ? ' override-active' : ''}`} aria-labelledby="currentMbaseRapPanelTitle">
                      <div className="mbase-rap-panel-head">
                        <div>
                          <div className="internal-eyebrow">Internal testing · Current Plan / Tier</div>
                          <h3 id="currentMbaseRapPanelTitle">MBASE / RAP Override</h3>
                          <p>Use detailed RAP inputs for this Plan / Tier. When enabled, historical retirement dates are allowed and the calculator automatically uses the RAP assumptions effective for that date. This testing control will not appear in the live member experience.</p>
                        </div>
                        <label className="internal-use-toggle" htmlFor="currentUseMbaseRapInputs">
                          <input id="currentUseMbaseRapInputs" type="checkbox" role="switch"
                            checked={mbaseOverrideActive}
                            onChange={(e) => vm.form.mbaseDetailOverrideEnabled = e.target.checked} />
                          <span className="internal-toggle-switch" aria-hidden="true" />
                          <span className="internal-toggle-copy">
                            <span className="internal-toggle-title">Use MBASE / RAP Override</span>
                            <span className="internal-toggle-status">{mbaseOverrideActive ? 'Override on' : 'Override off'}</span>
                          </span>
                        </label>
                      </div>
                      <div className="three-col internal-fields">
                        <div className="field">
                          <div className="field-label-slot">
                            <label htmlFor="currentMbaseIntegratedServiceHours">Integrated Service</label>
                          </div>
                          <div className="field-control-slot">
                            <div className="control">
                              <input id="currentMbaseIntegratedServiceHours" type="number" min={0} step="0.1" placeholder="0"
                                value={vm.form.mbaseIntegratedServiceHours ?? ''}
                                onChange={(e) => vm.form.mbaseIntegratedServiceHours = toNumberOrUndefined(e.target.value)} />
                              <span className="suffix">hours</span>
                            </div>
                          </div>
                        </div>
                        <div className="field">
                          <div className="field-label-slot">
                            <label htmlFor="currentMbaseNonIntegratedServiceHours">Non-Integrated Service</label>
                          </div>
                          <div className="field-control-slot">
                            <div className="control">
                              <input id="currentMbaseNonIntegratedServiceHours" type="number" min={0} step="0.1" placeholder="0"
                                value={vm.form.mbaseNonIntegratedServiceHours ?? ''}
                                onChange={(e) => vm.form.mbaseNonIntegratedServiceHours = toNumberOrUndefined(e.target.value)} />
                              <span className="suffix">hours</span>
                            </div>
                          </div>
                        </div>
                        <div className="field">
                          <div className="field-label-slot">
                            <label htmlFor="currentMbaseIntegratedSickHours">Sick Leave — Integrated</label>
                          </div>
                          <div className="field-control-slot">
                            <div className="control">
                              <input id="currentMbaseIntegratedSickHours" type="number" min={0} step="0.1" placeholder="0"
                                value={vm.form.mbaseIntegratedSickHours ?? ''}
                                onChange={(e) => vm.form.mbaseIntegratedSickHours = toNumberOrUndefined(e.target.value)} />
                              <span className="suffix">hours</span>
                            </div>
                          </div>
                        </div>
                        <div className="field">
                          <div className="field-label-slot">
                            <label htmlFor="currentMbaseNonIntegratedSickHours">Sick Leave — Non-Integrated</label>
                          </div>
                          <div className="field-control-slot">
                            <div className="control">
                              <input id="currentMbaseNonIntegratedSickHours" type="number" min={0} step="0.1" placeholder="0"
                                value={vm.form.mbaseNonIntegratedSickHours ?? ''}
                                onChange={(e) => vm.form.mbaseNonIntegratedSickHours = toNumberOrUndefined(e.target.value)} />
                              <span className="suffix">hours</span>
                            </div>
                          </div>
                        </div>
                        <div className="field">
                          <div className="field-label-slot">
                            <label htmlFor="currentMbaseRegularContributions">Regular Employee Contributions</label>
                          </div>
                          <div className="field-control-slot">
                            <div className="control">
                              <span className="prefix">$</span>
                              <input id="currentMbaseRegularContributions" type="text" inputMode="decimal" autoComplete="off" placeholder="100,000.00"
                                value={vm.form.mbaseRegularContributions ?? ''}
                                onChange={(e) => vm.form.mbaseRegularContributions = toNumberOrUndefined(e.target.value)} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                  )}

                  <div className="additional-membership-question">
                    <div className="question-with-help" id="priorMembershipQuestionLabel">
                      <span>Do you have service credit in another Plan / Tier?</span>
                      <button className="help-trigger" type="button" aria-label="Learn more about additional Plan or Tier service credit" aria-haspopup="dialog" onClick={() => setOpenHelpId('priorMembership')}>?</button>
                    </div>
                    <fieldset className="radio-group" aria-labelledby="priorMembershipQuestionLabel">
                      <div className="radio-row">
                        <label className="radio-option">
                          <input type="radio" name="hasPriorMembership" checked={hasPriorMembership} onChange={() => vm.setHasPriorMembership(true)} /><span className="radio-dot" aria-hidden="true" /><span>Yes</span>
                        </label>
                        <label className="radio-option">
                          <input type="radio" name="hasPriorMembership" checked={!hasPriorMembership} onChange={() => vm.setHasPriorMembership(false)} /><span className="radio-dot" aria-hidden="true" /><span>No</span>
                        </label>
                      </div>
                    </fieldset>
                  </div>

                  {hasPriorMembership && (
                    <div className="prior-membership-area" aria-live="polite">
                      <div className="prior-membership-head">
                        <div>
                          <h3>Prior Memberships</h3>
                          <p>Add each prior Plan / Tier and the service credit earned in it. Each membership is calculated separately and combined into one estimate.</p>
                        </div>
                      </div>
                      {vm.form.priorMemberships.map((m, i) => {
                        const ov = !!m.overrideEnabled;
                        const num = (k: 'integratedServiceHours' | 'nonIntegratedServiceHours' | 'integratedSickHours' | 'nonIntegratedSickHours' | 'regularContributions', v: string) => { m[k] = toNumberOrUndefined(v); };
                        const hoursField = (k: 'integratedServiceHours' | 'nonIntegratedServiceHours' | 'integratedSickHours' | 'nonIntegratedSickHours', label: string) => (
                          <div className="field">
                            <div className="field-label-slot"><label htmlFor={`${m.id}-${k}`}>{label}</label></div>
                            <div className="field-control-slot"><div className="control">
                              <input id={`${m.id}-${k}`} type="number" min={0} step="0.1" placeholder="0" value={m[k] ?? ''} onChange={(e) => num(k, e.target.value)} />
                              <span className="suffix">hours</span>
                            </div></div>
                          </div>
                        );
                        return (
                          <div className="prior-membership-row" key={m.id}>
                            <div className="field">
                              <span className="membership-index">Prior Membership {i + 1}</span>
                              <label htmlFor={`${m.id}-tier`}>Plan / Tier<sup className="req" aria-label="required">*</sup></label>
                              <select id={`${m.id}-tier`} required value={m.tier} onChange={(e) => { m.tier = e.target.value; }}>
                                <option disabled value="">Select</option>
                                {tierOptions.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                              </select>
                            </div>
                            <div className="field prior-service-wrap">
                              <label htmlFor={`${m.id}-service`}>Service Credit<sup className="req" aria-label="required">*</sup></label>
                              <div className="control">
                                <input id={`${m.id}-service`} type="text" inputMode="decimal" autoComplete="off" placeholder="5.00000" required={!ov} disabled={ov}
                                  value={ov ? '' : m.serviceRaw}
                                  onChange={(e) => { m.serviceRaw = e.target.value.replace(/,/g, ''); }} />
                                <span className="suffix">years</span>
                              </div>
                            </div>
                            <button type="button" className="outline remove-membership" aria-label={`Remove prior membership ${i + 1}`} onClick={() => vm.removePriorMembership(m.id)}>Remove</button>
                            {isDevEnvironment && (
                              <section className={`mbase-rap-panel membership-override-panel prior-mbase-panel${ov ? ' override-active' : ''}`}>
                                <div className="mbase-rap-panel-head">
                                  <div>
                                    <div className="internal-eyebrow">Internal testing · This Plan / Tier</div>
                                    <h3>MBASE / RAP Override</h3>
                                    <p>Use detailed RAP inputs for this membership component.</p>
                                  </div>
                                  <label className="internal-use-toggle" htmlFor={`${m.id}-ov`}>
                                    <input id={`${m.id}-ov`} type="checkbox" role="switch" checked={ov} onChange={(e) => { m.overrideEnabled = e.target.checked; }} />
                                    <span className="internal-toggle-switch" aria-hidden="true" />
                                    <span className="internal-toggle-copy">
                                      <span className="internal-toggle-title">Use MBASE / RAP Override</span>
                                      <span className="internal-toggle-status">{ov ? 'Override on' : 'Override off'}</span>
                                    </span>
                                  </label>
                                </div>
                                <div className="three-col internal-fields">
                                  {hoursField('integratedServiceHours', 'Integrated Service')}
                                  {hoursField('nonIntegratedServiceHours', 'Non-Integrated Service')}
                                  {hoursField('integratedSickHours', 'Sick Leave — Integrated')}
                                  {hoursField('nonIntegratedSickHours', 'Sick Leave — Non-Integrated')}
                                  <div className="field">
                                    <div className="field-label-slot"><label htmlFor={`${m.id}-rc`}>Regular Employee Contributions</label></div>
                                    <div className="field-control-slot"><div className="control">
                                      <span className="prefix">$</span>
                                      <input id={`${m.id}-rc`} type="text" inputMode="decimal" autoComplete="off" placeholder="100,000.00" value={m.regularContributions ?? ''} onChange={(e) => num('regularContributions', e.target.value)} />
                                    </div></div>
                                  </div>
                                </div>
                              </section>
                            )}
                          </div>
                        );
                      })}
                      <div className="prior-membership-add-wrap">
                        <button className="outline compact-action" type="button" onClick={() => vm.addPriorMembership()}>+ Add Another Plan / Tier</button>
                      </div>
                    </div>
                  )}
                </section>

                <section className="form-section" id="beneficiarySection">
                  <h2>2. Beneficiary</h2>
                  <div className="three-col">
                    <div className="field">
                      <div className="field-label-slot">
                        <div className="group-label" id="beneficiaryGroupLabel">Designate A Beneficiary For Survivor Continuance?</div>
                      </div>
                      <div className="field-control-slot">
                        <fieldset className="radio-group" aria-labelledby="beneficiaryGroupLabel">
                          <div className="radio-row">
                            <label className="radio-option">
                              <input type="radio" name="includeBen" checked={vm.form.includeBen} onChange={() => vm.form.includeBen = true} /><span className="radio-dot" aria-hidden="true" /><span>Yes</span>
                            </label>
                            <label className="radio-option">
                              <input type="radio" name="includeBen" checked={!vm.form.includeBen} onChange={() => vm.form.includeBen = false} /><span className="radio-dot" aria-hidden="true" /><span>No</span>
                            </label>
                          </div>
                        </fieldset>
                      </div>
                    </div>
                    <div className={`field${vm.form.includeBen ? '' : ' soft-disabled'}`}>
                      <div className="field-label-slot">
                        <label htmlFor="benRelationship" className="label-with-help">
                          <span>Relationship {vm.form.includeBen && <sup className="req" aria-label="required">*</sup>}</span>
                          {helpButton('benRelationship')}
                        </label>
                      </div>
                      <div className="field-control-slot">
                        <select id="benRelationship" required disabled={!vm.form.includeBen} value={vm.form.benRelationship ?? ''} onChange={(e) => vm.onInputChange(e, 'benRelationship', vm.form)}>
                          <option disabled value="">Select</option>
                          {vm.referenceData.beneficiaryRelationshipOptions.map(o => <option key={o.code} value={o.code}>{o.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className={`field${vm.form.includeBen ? '' : ' soft-disabled'}`}>
                      <div className="field-label-slot">
                        <label htmlFor="benDob">Date Of Birth {vm.form.includeBen && <sup className="req" aria-label="required">*</sup>}</label>
                      </div>
                      <div className="field-control-slot">
                        <input id="benDob" type="date" max="9999-12-31" required disabled={!vm.form.includeBen}
                          value={toDateInputValue(vm.form.benDob)}
                          onChange={(e) => vm.onInputChange(e, 'benDob', vm.form)} />
                      </div>
                    </div>
                  </div>
                </section>

                {taEligible && (
                  <section className="form-section" id="temporaryAnnuitySection">
                    <h2>3. Temporary Annuity <span style={{ fontSize: 15 }}>(Social Security Level-Income Estimate)</span></h2>
                    <div className="three-col">
                      <div className="field">
                        <div className="field-label-slot"><div className="group-label" id="temporaryAnnuityGroupLabel">Include Temporary Annuity?</div></div>
                        <div className="field-control-slot">
                          <fieldset className="radio-group" aria-labelledby="temporaryAnnuityGroupLabel">
                            <div className="radio-row">
                              <label className="radio-option">
                                <input type="radio" name="includeTA" checked={vm.form.includeTA} onChange={() => vm.form.includeTA = true} /><span className="radio-dot" aria-hidden="true" /><span>Yes</span>
                              </label>
                              <label className="radio-option">
                                <input type="radio" name="includeTA" checked={!vm.form.includeTA} onChange={() => vm.form.includeTA = false} /><span className="radio-dot" aria-hidden="true" /><span>No</span>
                              </label>
                            </div>
                          </fieldset>
                        </div>
                      </div>
                      <div className={`field${vm.form.includeTA ? '' : ' soft-disabled'}`}>
                        <div className="field-label-slot"><div className="static-field-label">Social Security Start Age</div></div>
                        <div className="field-control-slot"><div className="field-static-value" aria-label="Social Security Start Age 62">62</div></div>
                      </div>
                      <div className={`field${vm.form.includeTA ? '' : ' soft-disabled'}`}>
                        <div className="field-label-slot">
                          <label htmlFor="ssEstimate">Estimated Social Security Benefit {vm.form.includeTA && <sup className="req" aria-label="required">*</sup>}</label>
                        </div>
                        <div className="field-control-slot">
                          <div className="control">
                            <span className="prefix">$</span>
                            <input id="ssEstimate" type="text" inputMode="decimal" autoComplete="off" placeholder="2,100.00" required={vm.form.includeTA} disabled={!vm.form.includeTA}
                              value={vm.form.ssEstimate ?? ''}
                              onChange={(e) => vm.form.ssEstimate = toNumberOrUndefined(e.target.value)} />
                            <span className="suffix">/ month</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                )}

                <div className="form-bottom">
                  <button id="calcBtn" className="primary" type="submit" disabled={vm.isPreparingCalculation}>Calculate Estimate</button>
                </div>
                {isDevEnvironment && (
                  <p className="reference-data-source" data-source={vm.referenceDataSource}>
                    Internal testing · Reference data: {vm.referenceDataSource === 'server'
                      ? 'database'
                      : `bundled fallback${vm.referenceDataIssue ? ` (${vm.referenceDataIssue})` : ' (loading…)'}`}
                  </p>
                )}
              </form>

              {vm.calculationError && (
                <section className="form-section results-section" id="resultSection" aria-live="polite">
                  <div className="load-error-banner calc-error-banner">{vm.calculationError}</div>
                </section>
              )}
            </div>

            <aside className="sidebar">
              <section className="sidebar-card">
                <h3><span className="sidebar-heading-icon">{TOOL_DOES_ICON}</span><span>What This Tool Does</span></h3>
                <ul>
                  <li>Estimates your monthly retirement allowance under the available payment options.</li>
                  <li>Shows how your estimated benefits may change based on the information you provide.</li>
                  <li>Helps you plan for your retirement with confidence.</li>
                </ul>
                <div className="sidebar-sep" />
                <h3><span className="sidebar-heading-icon">{IMPORTANT_NOTES_ICON}</span><span>Important Notes</span></h3>
                <ul>
                  <li>This is a preliminary estimate only.</li>
                  <li>Your actual benefit is determined at retirement based on your final compensation, service credit, and SCERS rules in effect at that time.</li>
                </ul>
              </section>
              <section className="sidebar-card apply-card">
                <h3><span className="sidebar-heading-icon">{MYSCERS_ICON}</span><span>Do More with MySCERS</span></h3>
                <p>MySCERS gives you secure access to your retirement information and online services. Depending on your membership status, you can:</p>
                <ul>
                  <li>Create personalized retirement estimates using your SCERS account information.</li>
                  <li>View contribution history, vesting information, and Annual Statements.</li>
                  <li>Access available Pay Advices, 1099-Rs, and other retirement documents.</li>
                  <li>Update your contact information and apply for retirement online when you're ready.</li>
                </ul>
                <a className="apply-link" href="https://my.scers.gov/">Visit MySCERS&nbsp;&nbsp;→</a>
              </section>
            </aside>
          </div>
        </main>

        {openHelpId && <HelpModal content={help[openHelpId]} onClose={() => setOpenHelpId(null)} />}
      </div>
    </View>
  );
});

export default RetirementCalcView;
